import { Emitter, EmitterParams } from "./emitters";
import { ObjectTemplate, PlacedObject, valueOperatorKind } from "./objects";
import { PortId } from "./connections";
import { pickUndoSelectTarget, UndoSelectTarget } from "./undoSelect";
import { AppContext } from "./appContext";

export const UNDO_LIMIT = 100;

export type EntityKind = "obj" | "emitter" | "conn";

/**
 * One entity's inverse op: `before` is the serialized value to restore (undo →
 * upsert), or null when the entity did not exist before the gesture (undo →
 * remove). Applying a step touches only these entities, so the per-key diff sync
 * broadcasts only this footprint — a remote peer's concurrently-edited entities
 * are never collateral.
 */
export interface EntityDelta {
  kind: EntityKind;
  id: string;
  before: unknown | null;
}

export interface SettingsDelta {
  clockPeriod: number;
  gravityY: number;
}

/** Local selection captured by id (stable across delta apply, unlike an array index). */
export interface SelSnapshot {
  objId: string | null;
  emId: string | null;
}

/** One coalesced gesture's footprint: the touched entities' before-values plus any settings / simpleMode / selection change. */
export interface UndoStep {
  entities: EntityDelta[];
  settings?: SettingsDelta;
  simpleMode?: boolean;
  selBefore: SelSnapshot;
}

/** Full-scene serialization used for scene export (`sceneBundle`); not the undo unit. */
export interface SceneSnapshot {
  objects: unknown;
  objSel: number;
  emitters: unknown;
  emSel: number;
  connections: unknown;
  settings?: { clockPeriod: number; gravityY: number };
  simpleMode?: boolean;
}

interface UndoStackDeps {
  withParamBase<T>(fn: () => T): T;
  syncAudioFromObjects(): void;
  captureSettings(): { clockPeriod: number; gravityY: number };
  applySettings(settings: { clockPeriod: number; gravityY: number }): void;
  paramBase: Map<string, number>;
  objectById(id: string): PlacedObject | null;
  hasClipboard(): boolean;
  setUndoRedoEnabled(canUndo: boolean, canRedo: boolean): void;
  setCopyPasteEnabled(canCopy: boolean, canPaste: boolean): void;
  applyFeatureGates(): void;
  reconcileOrbitClockSync(): void;
  bindPanel(): void;
  persistObjects(): void;
  persistConnections(): void;
  syncEmitters(): void;
  clearEmitInjection(): void;
  resetSignalState(): void;
  primeSignalState(): void;
  getSimpleMode(): boolean;
  setSimpleMode(on: boolean): void;
}

export interface UndoStackApi {
  undoStack: UndoStep[];
  redoStack: UndoStep[];
  snapshot(): SceneSnapshot;
  syncUndoButtons(): void;
  syncCopyPasteButtons(): void;
  pushUndo(): void;
  pushSelectionUndo(): void;
  recordBefore(kind: EntityKind, id: string): void;
  runSuppressed<T>(fn: () => T): T;
  discardLastUndo(): void;
  reset(): void;
  undo(): void;
  undoAll(): void;
  redo(): void;
  beginEdit(token: string): void;
  resetEditGesture(): void;
  editObject(token: string, patch: Partial<ObjectTemplate>, spawnApply: () => void): void;
  pruneValuePorts(obj: PlacedObject): void;
  setNodeOperand(id: string, value: number): void;
  editEmitter(token: string, patch: Partial<EmitterParams>, spawnApply: () => void): void;
  editEmitterSeqSteps(em: Emitter, token: string, nextSteps: number[]): void;
}

/**
 * Per-entity (Command-model) undo/redo. A recording transaction is armed by
 * `beginEdit`/`pushUndo` and accumulates the before-value of every entity a
 * local mutator touches (the data structures call `recordBefore` themselves);
 * the next gesture / undo / redo commits it as one `UndoStep`. Remote applies and
 * the undo apply itself run under `runSuppressed`, so they never seed a step.
 * Restoring a step applies only its footprint's deltas, so the existing per-key
 * diff sync broadcasts only those keys (no collateral removes for entities a peer
 * added). The slider-drag coalescing (`editGesture`) and selection coalescing
 * (`selectionRun`) carry over from the snapshot era.
 */
export function initUndoStack(ctx: AppContext, deps: UndoStackDeps): UndoStackApi {
  const { field, emitterField, graph } = ctx;

  const undoStack: UndoStep[] = [];
  const redoStack: UndoStep[] = [];
  let editGesture: string | null = null;
  let selectionRun = false;

  let recording: Map<string, EntityDelta> | null = null;
  let recSelBefore: SelSnapshot = { objId: null, emId: null };
  let recSettingsBefore: SettingsDelta = { clockPeriod: 0, gravityY: 0 };
  let recSimpleBefore = false;
  let suppressed = false;

  function captureEntity(kind: EntityKind, id: string): unknown | null {
    return deps.withParamBase((): unknown | null =>
      kind === "obj"
        ? field.serializeById(id)
        : kind === "emitter"
          ? emitterField.serializeById(id)
          : graph.serializeById(id),
    );
  }

  function captureSelection(): SelSnapshot {
    return {
      objId: field.selectedObject()?.id ?? null,
      emId: emitterField.selectedEmitter()?.id ?? null,
    };
  }

  function recordBefore(kind: EntityKind, id: string): void {
    if (recording === null || suppressed) {
      return;
    }
    const key = `${kind}:${id}`;
    if (recording.has(key)) {
      return;
    }
    recording.set(key, { kind, id, before: captureEntity(kind, id) });
  }

  function runSuppressed<T>(fn: () => T): T {
    const prev = suppressed;
    suppressed = true;
    try {
      return fn();
    } finally {
      suppressed = prev;
    }
  }

  function snapshot(): SceneSnapshot {
    return deps.withParamBase((): SceneSnapshot => ({
      objects: field.serialize(),
      objSel: field.selectedIndex(),
      emitters: emitterField.serialize(),
      emSel: emitterField.selectedIndex(),
      connections: graph.serialize(),
      settings: deps.captureSettings(),
      simpleMode: deps.getSimpleMode(),
    }));
  }

  function syncUndoButtons(): void {
    deps.setUndoRedoEnabled(undoStack.length > 0 || recording !== null, redoStack.length > 0);
  }

  function syncCopyPasteButtons(): void {
    const hasSelection = field.selectedObject() !== null || emitterField.selectedEmitter() !== null;
    deps.setCopyPasteEnabled(hasSelection, deps.hasClipboard());
  }

  /**
   * Opens a fresh recording for the gesture about to run and clears the redo
   * stack (a new edit invalidates redo, matching the snapshot-era eager clear).
   */
  function arm(): void {
    recording = new Map<string, EntityDelta>();
    recSelBefore = captureSelection();
    recSettingsBefore = deps.captureSettings();
    recSimpleBefore = deps.getSimpleMode();
    redoStack.length = 0;
  }

  /**
   * Finalizes the open recording into one step, pushed only when it changed
   * something (entities / settings / simpleMode / selection); a no-op gesture is
   * dropped so it never consumes an undo.
   */
  function commitRecording(): void {
    if (recording === null) {
      return;
    }
    const entities = [...recording.values()];
    recording = null;
    const curSettings = deps.captureSettings();
    const settingsChanged =
      curSettings.clockPeriod !== recSettingsBefore.clockPeriod || curSettings.gravityY !== recSettingsBefore.gravityY;
    const simpleChanged = deps.getSimpleMode() !== recSimpleBefore;
    const curSel = captureSelection();
    const selChanged = curSel.objId !== recSelBefore.objId || curSel.emId !== recSelBefore.emId;
    if (entities.length === 0 && !settingsChanged && !simpleChanged && !selChanged) {
      return;
    }
    const step: UndoStep = { entities, selBefore: recSelBefore };
    if (settingsChanged) {
      step.settings = recSettingsBefore;
    }
    if (simpleChanged) {
      step.simpleMode = recSimpleBefore;
    }
    undoStack.push(step);
    if (undoStack.length > UNDO_LIMIT) {
      undoStack.shift();
    }
  }

  function pushUndo(): void {
    selectionRun = false;
    commitRecording();
    editGesture = null;
    arm();
    syncUndoButtons();
  }

  /**
   * Opens a coalescing selection run: the first selection after any edit (or
   * undo/redo) arms the run, further selections fold into it until an edit's
   * pushUndo clears the run. Callers invoke this before the selection mutation
   * and only when the selection actually changes.
   */
  function pushSelectionUndo(): void {
    if (selectionRun) {
      return;
    }
    commitRecording();
    editGesture = null;
    arm();
    selectionRun = true;
    syncUndoButtons();
  }

  function beginEdit(token: string): void {
    if (editGesture === token) {
      return;
    }
    selectionRun = false;
    commitRecording();
    arm();
    editGesture = token;
    syncUndoButtons();
  }

  function resetEditGesture(): void {
    editGesture = null;
  }

  function isTransformParentType(type: string): boolean {
    return type === "group" || type === "orbit";
  }

  /**
   * The entity to select after applying a step: among the step's surviving
   * entities (before !== null), a transform parent if any, else the first object,
   * else the first emitter. Null when the step revived no entity, which keeps the
   * recorded `selBefore`.
   */
  function selectAfter(step: UndoStep): UndoSelectTarget | null {
    const objects = step.entities
      .filter((d: EntityDelta): boolean => d.kind === "obj" && d.before !== null)
      .map((d: EntityDelta): unknown => d.before);
    const emitters = step.entities
      .filter((d: EntityDelta): boolean => d.kind === "emitter" && d.before !== null)
      .map((d: EntityDelta): unknown => d.before);
    return pickUndoSelectTarget({ objects: [], emitters: [] }, { objects, emitters }, isTransformParentType);
  }

  function applySelection(step: UndoStep): void {
    const target = selectAfter(step);
    if (target !== null) {
      if (target.kind === "object") {
        field.selectIndex(field.list().findIndex((o: PlacedObject): boolean => o.id === target.id));
        emitterField.selectIndex(-1);
      } else {
        emitterField.selectIndex(emitterField.list().findIndex((e: Emitter): boolean => e.id === target.id));
        field.selectIndex(-1);
      }
      return;
    }
    field.selectIndex(
      step.selBefore.objId === null
        ? -1
        : field.list().findIndex((o: PlacedObject): boolean => o.id === step.selBefore.objId),
    );
    emitterField.selectIndex(
      step.selBefore.emId === null
        ? -1
        : emitterField.list().findIndex((e: Emitter): boolean => e.id === step.selBefore.emId),
    );
  }

  function applyEntities(deltas: EntityDelta[]): void {
    for (const d of deltas) {
      if (d.kind === "obj") {
        if (d.before === null) {
          field.removeById(d.id);
        } else {
          field.updateFromSerialized(d.id, d.before);
        }
      } else if (d.kind === "emitter") {
        if (d.before === null) {
          emitterField.removeById(d.id);
        } else {
          emitterField.updateFromSerialized(d.id, d.before);
        }
      } else if (d.before === null) {
        graph.removeById(d.id);
      } else {
        graph.upsertSerialized(d.before);
      }
    }
  }

  /** Captures the step's entities' / settings' current values as the reverse step; must run before `applyStep`. */
  function invertStep(step: UndoStep): UndoStep {
    const entities = step.entities.map((d: EntityDelta): EntityDelta => ({
      kind: d.kind,
      id: d.id,
      before: captureEntity(d.kind, d.id),
    }));
    const inverse: UndoStep = { entities, selBefore: captureSelection() };
    if (step.settings !== undefined) {
      inverse.settings = deps.captureSettings();
    }
    if (step.simpleMode !== undefined) {
      inverse.simpleMode = deps.getSimpleMode();
    }
    return inverse;
  }

  /**
   * Applies a step's deltas to the live scene and reconciles the dependent engine
   * state, suppressed so the apply itself records no new step. The persist calls
   * fire the per-key diff sync, which broadcasts only this step's footprint.
   */
  function applyStep(step: UndoStep): void {
    runSuppressed((): void => {
      deps.paramBase.clear();
      deps.resetSignalState();
      applyEntities(step.entities);
      if (step.simpleMode !== undefined) {
        deps.setSimpleMode(step.simpleMode);
      }
      applySelection(step);
      deps.primeSignalState();
      deps.clearEmitInjection();
      deps.applyFeatureGates();
      if (step.settings !== undefined) {
        deps.applySettings(step.settings);
      }
      deps.reconcileOrbitClockSync();
      deps.syncAudioFromObjects();
      deps.bindPanel();
      deps.persistObjects();
      deps.syncEmitters();
      deps.persistConnections();
    });
  }

  /** True when applying the step would change nothing visible (entities/settings/simpleMode/selection all already match). */
  function stepIsNoop(step: UndoStep): boolean {
    for (const d of step.entities) {
      if (JSON.stringify(captureEntity(d.kind, d.id)) !== JSON.stringify(d.before)) {
        return false;
      }
    }
    if (step.settings !== undefined) {
      const cur = deps.captureSettings();
      if (cur.clockPeriod !== step.settings.clockPeriod || cur.gravityY !== step.settings.gravityY) {
        return false;
      }
    }
    if (step.simpleMode !== undefined && deps.getSimpleMode() !== step.simpleMode) {
      return false;
    }
    const sel = captureSelection();
    return sel.objId === step.selBefore.objId && sel.emId === step.selBefore.emId;
  }

  /** Pops `stack` until a step that would visibly change the scene; no-op steps drain and are discarded. */
  function popVisible(stack: UndoStep[]): UndoStep | null {
    while (stack.length > 0) {
      const step = stack.pop() as UndoStep;
      if (!stepIsNoop(step)) {
        return step;
      }
    }
    return null;
  }

  function undo(): void {
    selectionRun = false;
    commitRecording();
    const step = popVisible(undoStack);
    if (step === null) {
      syncUndoButtons();
      return;
    }
    const inverse = invertStep(step);
    applyStep(step);
    redoStack.push(inverse);
    editGesture = null;
    syncUndoButtons();
  }

  /**
   * Drains the undo stack with repeated `undo()` so the whole local session rolls
   * back. The kicked-from-room flow calls this before disconnecting: each `undo()`
   * applies its footprint and fires the per-key diff sync, so every removal/restore
   * broadcasts to the room. The in-flight gesture is committed first so the session's
   * last (still-open) action is reverted too — without it a kick landing right after
   * an edit would leave that edit standing. Bounded by the stack (UNDO_LIMIT) and
   * local-session only — a reload-dropped or peer-overwritten (LWW) edit is out of reach.
   */
  function undoAll(): void {
    commitRecording();
    while (undoStack.length > 0) {
      undo();
    }
  }

  function redo(): void {
    selectionRun = false;
    commitRecording();
    const step = popVisible(redoStack);
    if (step === null) {
      syncUndoButtons();
      return;
    }
    const inverse = invertStep(step);
    applyStep(step);
    undoStack.push(inverse);
    if (undoStack.length > UNDO_LIMIT) {
      undoStack.shift();
    }
    editGesture = null;
    syncUndoButtons();
  }

  /**
   * Cancels the last undo entry. Drops the still-open recording when a gesture is
   * in flight (drag/connect cancel), else pops the last committed step. Used by
   * the group-delete "cancel" and the connect-rejected paths.
   */
  function discardLastUndo(): void {
    if (recording !== null) {
      recording = null;
      editGesture = null;
    } else {
      undoStack.pop();
    }
    syncUndoButtons();
  }

  /** Clears all undo/redo state. Called on a full-scene swap (scene load / room snapshot) where per-entity deltas no longer map onto the live scene. */
  function reset(): void {
    undoStack.length = 0;
    redoStack.length = 0;
    recording = null;
    editGesture = null;
    selectionRun = false;
    syncUndoButtons();
  }

  function editObject(token: string, patch: Partial<ObjectTemplate>, spawnApply: () => void): void {
    const sel = field.selectedObject();
    if (sel) {
      beginEdit(token);
      field.editSelected(patch);
      deps.bindPanel();
      deps.persistObjects();
    } else {
      spawnApply();
    }
  }

  /**
   * Drops the edges a calc node loses when its operator switches to a kind that
   * no longer exposes them, so the switch never leaves a wire dangling on a
   * hidden pin: a unary op drops operand2In, a random op (pulse-in + value-in)
   * drops only operand2In, and switching away from random drops its pulse-in
   * (inc). The preceding editObject recording already captured the pre-switch
   * object and the disconnect recorder captures each dropped edge, so undo
   * restores them with the operator.
   */
  function pruneValuePorts(obj: PlacedObject): void {
    const kind = valueOperatorKind(obj.operator);
    const drop: PortId[] = kind === "random"
      ? ["operand2In"]
      : kind === "unary"
        ? ["operand2In", "inc"]
        : ["inc"];
    let changed = false;
    for (const port of drop) {
      for (const e of graph.edgesTo(obj.id, port)) {
        graph.disconnect(e);
        changed = true;
      }
    }
    if (changed) {
      deps.persistConnections();
      deps.bindPanel();
    }
  }

  /**
   * Writes a calc node's typed operand by id — the inline operand-2 field at its
   * port edits this. One undo per commit; no-op when unchanged or not a calc node.
   * The operand is set directly (not via a recording mutator), so the footprint is
   * recorded explicitly.
   */
  function setNodeOperand(id: string, value: number): void {
    const obj = deps.objectById(id);
    if (!obj || obj.type !== "calc" || obj.operand === value) {
      return;
    }
    pushUndo();
    recordBefore("obj", id);
    obj.operand = value;
    deps.persistObjects();
    deps.bindPanel();
  }

  function editEmitter(token: string, patch: Partial<EmitterParams>, spawnApply: () => void): void {
    if (emitterField.selectedEmitter()) {
      beginEdit(token);
      emitterField.editSelected(patch);
      deps.syncEmitters();
      deps.bindPanel();
    } else {
      spawnApply();
    }
  }

  function editEmitterSeqSteps(em: Emitter, token: string, nextSteps: number[]): void {
    const idx = emitterField.list().indexOf(em);
    if (idx < 0) {
      return;
    }
    emitterField.selectIndex(idx);
    editEmitter(token, { seqSteps: nextSteps }, (): void => {});
  }

  field.setUndoRecorder((id: string): void => recordBefore("obj", id));
  emitterField.setUndoRecorder((id: string): void => recordBefore("emitter", id));
  graph.setUndoRecorder((id: string): void => recordBefore("conn", id));

  return {
    undoStack,
    redoStack,
    snapshot,
    syncUndoButtons,
    syncCopyPasteButtons,
    pushUndo,
    pushSelectionUndo,
    recordBefore,
    runSuppressed,
    discardLastUndo,
    reset,
    undo,
    undoAll,
    redo,
    beginEdit,
    resetEditGesture,
    editObject,
    pruneValuePorts,
    setNodeOperand,
    editEmitter,
    editEmitterSeqSteps,
  };
}
